import React, { useState, useEffect } from 'react';
import { useToast } from '../../components/ui/Toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import UniversalNavbar from '../../components/ui/UniversalNavbar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import QuestionBuilder from './components/QuestionBuilder';
import QuestionList from './components/QuestionList';
import SchemaPreview from './components/SchemaPreview';
import reviewCriteriaService from '../../services/reviewCriteriaService';
import conferenceService from '../../services/conferenceService';

const ReviewSchemaBuilder = () => {
  // Replaces alert(): non-blocking, styled, translatable, and errors
  // persist until dismissed instead of vanishing.
  const { success: toastSuccess, error: toastError } = useToast();
  const { t } = useTranslation('reviews');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const conferenceId = searchParams?.get('conferenceId');
  const { user } = useAuth();
  const [conference, setConference] = useState(null);
  const [criteria, setCriteria] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/user-sign-in');
    } else if (conferenceId) {
      loadData();
    } else {
      setError(t('schemaBuilder.noConferenceId'));
      setLoading(false);
    }
  }, [user?.id, conferenceId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [confData, criteriaData] = await Promise.all([
        conferenceService?.getById(conferenceId),
        reviewCriteriaService?.getByConference(conferenceId)
      ]);
      setConference(confData);
      setCriteria(criteriaData || []);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddQuestion = (newQuestion) => {
    const question = {
      ...newQuestion,
      id: `temp-${Date.now()}`,
      isNew: true
    };
    setCriteria(prev => [...prev, { ...question, displayOrder: prev.length }]);
  };

  const handleUpdateQuestion = (questionId, updates) => {
    setCriteria(prev => prev.map(q => q?.id === questionId ? { ...q, ...updates } : q));
    setEditingQuestion(null);
  };

  const handleDeleteQuestion = (questionId) => {
    setCriteria(prev => prev.filter(q => q?.id !== questionId));
    if (editingQuestion?.id === questionId) setEditingQuestion(null);
  };

  const handleReorderQuestions = (reorderedCriteria) => {
    setCriteria(reorderedCriteria?.map((q, index) => ({ ...q, displayOrder: index })));
  };

  const handleSaveSchema = async () => {
    try {
      setSaving(true);
      setError('');

      if (criteria?.length === 0) {
        setError(t('schemaBuilder.validation.addAtLeastOneQuestion'));
        return;
      }

      await reviewCriteriaService?.deleteByConference(conferenceId);

      const criteriaToSave = criteria?.map((q, index) => ({
        conferenceId,
        questionText: q?.questionText,
        questionType: q?.questionType,
        isRequired: q?.isRequired !== false,
        helpText: q?.helpText || null,
        scaleMin: q?.scaleMin || null,
        scaleMax: q?.scaleMax || null,
        scaleMinLabel: q?.scaleMinLabel || null,
        scaleMaxLabel: q?.scaleMaxLabel || null,
        options: q?.options || [],
        displayOrder: index
      }));

      await reviewCriteriaService?.createMultiple(conferenceId, criteriaToSave);
      toastSuccess(t('schemaBuilder.alerts.schemaSaved'));
      await loadData();
    } catch (err) {
      setError(err?.message);
      toastError(t('schemaBuilder.alerts.saveError', { error: err?.message }));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader" size={48} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">{t('schemaBuilder.loading')}</p>
        </div>
      </div>
    );
  }

  if (error && !conference) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
          <p className="text-error mb-4">{error}</p>
          <Button onClick={() => navigate('/user-dashboard')}>{t('schemaBuilder.backToDashboard')}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <UniversalNavbar />
      <main className="page-shell py-8">
            <div className="mb-6">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/conference-setup-wizard?draftId=${conferenceId}`)}
                iconName="ArrowLeft"
                iconPosition="left"
              >
                {t('schemaBuilder.backToConferenceSetup')}
              </Button>
            </div>

            {/* Header */}
            <div className="bg-card rounded-lg border border-border p-5 mb-6">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <h1 className="text-2xl font-bold text-foreground mb-1">{t('schemaBuilder.title')}</h1>
                  <p className="text-muted-foreground">{conference?.name}</p>
                  <p className="text-sm text-muted-foreground mt-0.5">{t('schemaBuilder.subtitle')}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowPreview(v => !v)}
                    iconName="Eye"
                    iconPosition="left"
                  >
                    {showPreview ? 'Hide Preview' : 'Preview'}
                  </Button>
                  <Button
                    onClick={handleSaveSchema}
                    disabled={saving || criteria?.length === 0}
                    iconName="Save"
                    iconPosition="left"
                  >
                    {saving ? t('schemaBuilder.saving') : t('schemaBuilder.saveSchema')}
                  </Button>
                </div>
              </div>
            </div>

            {error && (
              <div className="bg-error/10 border border-error/20 rounded-lg p-4 mb-6 flex items-center gap-2">
                <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0" />
                <p className="text-error text-sm">{error}</p>
              </div>
            )}

            {showPreview ? (
              <SchemaPreview criteria={criteria} />
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                {/* Left: Questions list */}
                <div className="lg:col-span-2">
                  <QuestionList
                    criteria={criteria}
                    onEditQuestion={setEditingQuestion}
                    onDeleteQuestion={handleDeleteQuestion}
                    onReorder={handleReorderQuestions}
                    editingQuestionId={editingQuestion?.id}
                  />
                </div>

                {/* Right: Add / Edit form */}
                <div className="lg:col-span-3">
                  <QuestionBuilder
                    editingQuestion={editingQuestion}
                    onAddQuestion={handleAddQuestion}
                    onUpdateQuestion={handleUpdateQuestion}
                    onCancelEdit={() => setEditingQuestion(null)}
                  />
                </div>
              </div>
            )}

            <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 mt-6">
              <div className="flex gap-3">
                <Icon name="Info" size={24} className="text-warning flex-shrink-0" />
                <div>
                  <h4 className="text-base font-semibold text-foreground mb-2">Important Notes</h4>
                  <ul className="space-y-1.5 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                      <span>A mandatory "General Comments" field is automatically added to all review forms</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                      <span>Questions appear in the order shown in the list. Use the arrows to reorder.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                      <span>Save your schema before leaving this page to preserve your changes</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
      </main>
    </div>
  );
};

export default ReviewSchemaBuilder;
